import {
  CustomIconCancel,
  CustomIconConfirmed,
  IconWrapper,
  OverviewContainer,
  StatusTitle,
  StatusTitleWrapper,
  StyledStatusIcon,
  Subtitle,
  TitleContainer,
} from './styles';
import { formatDate } from 'utils/formatDate';
import { BoldText } from 'hocs/bold-text';
import React, { useMemo } from 'react';
import { TransactionDetails } from 'store/transactions/types';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { selectPatchTransactionsStatus, selectTypeOfPatchTransaction } from 'store/transactions/selectors';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';
import { useSelector } from 'react-redux';
import { ACTION_STATUSES, BOOKING_STATUSES, PATCH_STATUSES } from 'shared/consts';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { searchParams } from 'shared/types/search-params';
import { selectCart } from 'store/cart/selectors';
import { formatStartDatesQueryParams } from 'components/payment/quick-checkout/components/multiday-checkout-content/utils/formatStartDatesQueryParam';
import { joinArrayItemsWithOxfordComma } from 'components/payment/quick-checkout/components/multiday-checkout-content/utils/joinItemsWithOxfordComma';
import { isBookingsForOneResource } from 'components/payment/receipt/components/utils/isBookingsForOneResource';
import { BookingStatus } from 'store/bookings/types';
import { isBookingCancelled } from 'components/payment/receipt/components/utils/booking-actionability.util';

interface ReceiptOverviewProps {
  transaction: TransactionDetails;
  isSwipeModalContent?: boolean;
}

export const ReceiptOverview = ({ transaction, isSwipeModalContent = false }: ReceiptOverviewProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const patchTransactionStatus = useSelector(selectPatchTransactionsStatus);
  const typeOfPatchTransaction = useSelector(selectTypeOfPatchTransaction);
  const patchTransactionData = useMemo(
    () => ({
      status: patchTransactionStatus,
      type: typeOfPatchTransaction,
    }),
    [patchTransactionStatus, typeOfPatchTransaction],
  );
  const buildingTimeZone = useSelector(selectBuildingTimeZone);
  const cart = useSelector(selectCart);
  const { startDates } = useSearchParams<searchParams>();

  const multidayBookingInfo = useMemo<{ resourceName: string; status: string } | null>(() => {
    const isMultidayBooking = isBookingsForOneResource(cart?.items);

    return !!startDates?.length && isMultidayBooking
      ? { resourceName: cart?.items[0].resource_booking.name, status: cart.items[0].status }
      : null;
  }, [cart?.items, startDates?.length]);

  const { formattedFullDate } = formatDate(transaction?.details.start_at, locale as string, buildingTimeZone);

  const hideIcon = useMemo<boolean>(
    () =>
      multidayBookingInfo
        ? multidayBookingInfo.status === BookingStatus.PENDING
        : transaction?.details.status === BookingStatus.PENDING && patchTransactionStatus !== ACTION_STATUSES.FULFILLED,
    [multidayBookingInfo, transaction?.details.status, patchTransactionStatus],
  );
  // The status the title and the icons all read from, so they cannot disagree with each other.
  const bookingStatus = multidayBookingInfo ? multidayBookingInfo.status : transaction?.details.status;

  /*
   * Cancelled either because this session just cancelled it, OR because it simply IS cancelled. The
   * second half is what the activity-center deep link needs: it opens a booking that was cancelled
   * elsewhere, so there is no patch state to read and the receipt used to render as if the booking
   * were still live.
   */
  const isCancelledIcon = useMemo<boolean>(
    () =>
      (patchTransactionStatus === ACTION_STATUSES.FULFILLED &&
        patchTransactionData.type === PATCH_STATUSES.CANCELLED) ||
      isBookingCancelled(bookingStatus),
    [patchTransactionStatus, patchTransactionData.type, bookingStatus],
  );

  const formattedDatesString = formatStartDatesQueryParams(startDates, locale);
  const andSeparator = intl.formatMessage({ id: 'and' });

  const formattedDatesStringWithSeparator = joinArrayItemsWithOxfordComma(formattedDatesString, andSeparator);

  const getReceiptTitle = (status: string, cancelled: boolean) => {
    if (cancelled) {
      return 'resource_cancelled';
    }
    if (status === BOOKING_STATUSES.ACCEPTED) {
      return 'booking_confirmed';
    }
    if (status === BOOKING_STATUSES.PENDING || status === BOOKING_STATUSES.PAYMENT_PENDING) {
      return 'resource_requested';
    }
    return 'default_receipt';
  };

  const getReceiptSubtitle = (status: string, cancelled: boolean) => {
    if (cancelled) {
      return 'cancelled_text';
    }
    if (status === BOOKING_STATUSES.ACCEPTED) {
      return 'confirmation_text';
    }
    if (status === BOOKING_STATUSES.PENDING || status === BOOKING_STATUSES.PAYMENT_PENDING) {
      return 'requested_subtitle';
    }
    return 'default_receipt';
  };

  const getOverviewTitle = () => {
    return {
      title: getReceiptTitle(bookingStatus, isCancelledIcon),
      subtitle: multidayBookingInfo
        ? intl.formatMessage(
            {
              id: getReceiptSubtitle(bookingStatus, isCancelledIcon),
            },
            {
              resourceName: <BoldText>{multidayBookingInfo.resourceName}</BoldText>,
              bookingTime: <BoldText>{formattedDatesStringWithSeparator}</BoldText>,
            },
          )
        : intl.formatMessage(
            {
              id: getReceiptSubtitle(bookingStatus, isCancelledIcon),
            },
            {
              resourceName: <BoldText>{transaction?.details.name}</BoldText>,
              bookingTime: <BoldText>{formattedFullDate}</BoldText>,
            },
          ),
    };
  };
  return (
    <OverviewContainer isSwipeModalContent={isSwipeModalContent} data-testid="receipt-overview">
      <TitleContainer>
        <StatusTitleWrapper isCancelledIcon={isCancelledIcon} data-testid="status-title-wrapper">
          <StatusTitle data-testid="booking-status-text">
            {intl.formatMessage({ id: getOverviewTitle().title })}
          </StatusTitle>
          {!hideIcon && (
            <IconWrapper>
              <StyledStatusIcon isCancelledIcon={isCancelledIcon} />
              {isCancelledIcon && (
                <CustomIconCancel isCancelledIcon={isCancelledIcon} data-testid="custom-cancel-icon" />
              )}
              {multidayBookingInfo === null && (
                <CustomIconConfirmed isCancelledIcon={isCancelledIcon} data-testid="custom-confirmed-icon" />
              )}
              {multidayBookingInfo && cart.items.every(booking => booking.status === BookingStatus.ACCEPTED) && (
                <CustomIconConfirmed isCancelledIcon={isCancelledIcon} data-testid="custom-confirmed-icon" />
              )}
            </IconWrapper>
          )}
        </StatusTitleWrapper>
      </TitleContainer>
      <Subtitle>{getOverviewTitle().subtitle}</Subtitle>
    </OverviewContainer>
  );
};
